// ============================================
// Media Utilities  
// DRY: Common media-related helper functions
// ============================================

import type { MediaItem, MediaDetails } from '../types';

/**
 * Get media title (works for both movies and TV shows)
 */
export function getMediaTitle(media: MediaItem | MediaDetails): string {
    return 'title' in media ? media.title : 'name' in media ? media.name : 'Unknown';
}

/**
 * Get media release date (works for both movies and TV shows)
 */
export function getMediaReleaseDate(media: MediaItem | MediaDetails): string | undefined {
    return 'release_date' in media ? media.release_date : 'first_air_date' in media ? media.first_air_date : undefined;
}

/**
 * Get media year
 */
export function getMediaYear(media: MediaItem | MediaDetails): number | null {
    const dateString = getMediaReleaseDate(media);
    if (!dateString) return null;

    try {
        return new Date(dateString).getFullYear();
    } catch {
        return null;
    }
}

/**
 * Build video player URL
 */
export function getVideoUrl(
    baseUrl: string,
    type: 'movie' | 'tv',
    id: number,
    season?: number,
    episode?: number
): string {
    if (type === 'movie') {
        return `${baseUrl}/movie/${id}`;
    }

    if (season !== undefined && episode !== undefined) {
        return `${baseUrl}/tv/${id}/${season}/${episode}`;
    }

    return `${baseUrl}/tv/${id}/1/1`; // Default to S01E01
}

/**
 * Check if media has valid poster
 */
export function hasValidPoster(media: MediaItem | MediaDetails): boolean {
    return media.poster_path !== null && media.poster_path !== undefined;
}

/**
 * Check if media has valid backdrop
 */
export function hasValidBackdrop(media: MediaItem | MediaDetails): boolean {
    return media.backdrop_path !== null && media.backdrop_path !== undefined;
}

/**
 * Get media rating color based on score
 */
export function getRatingColor(rating: number): string {
    if (rating >= 8) return 'text-green-400';
    if (rating >= 6) return 'text-yellow-400';
    if (rating >= 4) return 'text-orange-400';
    return 'text-red-400';
}

/**
 * Filter media by type
 */
export function filterByType<T extends MediaItem>(items: MediaItem[], type: 'movie' | 'tv'): T[] {
    return items.filter(item => item.media_type === type) as T[];
}

/**
 * Sort media by rating
 */
export function sortByRating(items: MediaItem[], descending: boolean = true): MediaItem[] {
    return [...items].sort((a, b) =>
        descending
            ? b.vote_average - a.vote_average
            : a.vote_average - b.vote_average
    );
}

/**
 * Sort media by popularity
 */
export function sortByPopularity(items: MediaItem[], descending: boolean = true): MediaItem[] {
    return [...items].sort((a, b) =>
        descending
            ? b.popularity - a.popularity
            : a.popularity - b.popularity
    );
}

/**
 * Sort media by release date
 */
export function sortByDate(items: MediaItem[], descending: boolean = true): MediaItem[] {
    return [...items].sort((a, b) => {
        const dateA = getMediaReleaseDate(a);
        const dateB = getMediaReleaseDate(b);

        if (!dateA || !dateB) return 0;

        const timeA = new Date(dateA).getTime();
        const timeB = new Date(dateB).getTime();

        return descending ? timeB - timeA : timeA - timeB;
    });
}
